import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Popover, TextField, Tooltip, Typography } from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import { useMdiIcons } from '../hooks/use-mdi-icons';
import { getMdiIconPath, MDI_VIEWBOX_SIZE, searchMdiIcons } from '../utils/mdi-icons';

interface Props {
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly onPick: (iconName: string) => void;
  /**
   * What to open on, before anything is searched for.
   *
   * Given by the caller because the useful shortlist depends on what is being labelled: a
   * wayfinding annotation is a fire extinguisher or a toilet, and a desk is a monitor or a
   * phone booth. Both reach the whole set by typing.
   */
  readonly suggested?: readonly string[];
}

/**
 * A shortlist of icons that actually get used on an office floorplan, so the picker opens
 * on something useful rather than on the first few of seven thousand alphabetically.
 * Searching reaches the whole set.
 */
const SUGGESTED_ICONS = [
  'printer', 'fire-extinguisher', 'coffee', 'water', 'stairs', 'elevator', 'toilet',
  'door', 'wheelchair-accessibility', 'silverware-fork-knife', 'sofa', 'television',
  'phone', 'wifi', 'projector', 'whiteboard', 'trash-can', 'bike', 'car', 'first-aid-kit',
  'account-group', 'lock', 'key', 'package-variant', 'flower', 'bell', 'exit-run', 'shower',
];

const MAXIMUM_RESULTS = 60;

const IconPicker = ({ anchorEl, onClose, onPick, suggested = SUGGESTED_ICONS }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const iconsLoaded = useMdiIcons();
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    if (!iconsLoaded) {
      return [];
    }

    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return suggested.filter((name) => getMdiIconPath(name));
    }

    return searchMdiIcons(trimmed, MAXIMUM_RESULTS);
  }, [query, iconsLoaded, suggested]);

  const typedName = query.trim().replace(/^mdi-/, '');
  const typedIsReal = getMdiIconPath(typedName) !== undefined;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <TextField
          fullWidth
          autoFocus
          size="small"
          label={String(t('floorplan-editor-icon-name'))}
          value={query}
          onChange={(event): void => setQuery(event.target.value)}
          onKeyDown={(event): void => {
            if (event.key === 'Enter' && typedIsReal) {
              onPick(typedName);
            }
          }}
          helperText={String(t('floorplan-editor-icon-name-help'))}
        />

        {query.trim() && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            {typedIsReal ? (
              <>
                <svg width={24} height={24} viewBox={`0 0 ${MDI_VIEWBOX_SIZE} ${MDI_VIEWBOX_SIZE}`}>
                  <path d={getMdiIconPath(typedName)} fill={StyleConstants.DARK_BLUE_COLOR} />
                </svg>
                <Typography variant="caption">{typedName}</Typography>
              </>
            ) : (
              <Typography variant="caption" color={StyleConstants.PINK_COLOR}>
                {t('floorplan-editor-icon-unknown')}
              </Typography>
            )}
          </Box>
        )}

        {/* Scrolls: the desk shortlist runs to ninety icons, which is fifteen rows, and a
            popover that grows past the bottom of the window puts its own contents out of
            reach. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 0.5,
            mt: 2,
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {results.map((name) => (
            <Tooltip key={name} title={name}>
              <Box
                component="button"
                type="button"
                aria-label={name}
                onClick={(): void => onPick(name)}
                sx={{
                  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
                  borderRadius: 1,
                  background: 'none',
                  cursor: 'pointer',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width={20} height={20} viewBox={`0 0 ${MDI_VIEWBOX_SIZE} ${MDI_VIEWBOX_SIZE}`}>
                  <path d={getMdiIconPath(name)} fill={StyleConstants.GREY_COLOR} />
                </svg>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Popover>
  );
};

export default IconPicker;
